// frontend/src/components/orcamento/AnaliseCategoria.js
// Painel lateral de UMA categoria (despesa ou receita): série de 36 m com outliers,
// cortes (3/6/12 m, mediana, YoY), calculadora percentual (base × +X %) e lista de títulos.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Scatter } from 'recharts';

const API = process.env.REACT_APP_API_BASE;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('access')}` });
const fmt = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v || 0);
// 'YYYY-MM-DD' → 'DD/MM/YYYY' (só para exibir)
const fmtData = (iso) => (iso && /^\d{4}-\d{2}-\d{2}/.test(iso)) ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : (iso || '');

// altura do painel quando solto (arrastado): não ocupa a tela toda; dá para redimensionar pelo canto
const ALTURA_FLUTUANTE = () => Math.min(Math.round(window.innerHeight * 0.8), 720);

// Dica do gráfico: só Valor/Outlier (o Scatter também expõe o eixo `mes`, que virava 'R$ NaN')
function DicaGrafico({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const itens = payload.filter((p) => (p.dataKey === 'valor' || p.dataKey === 'outlier') && Number.isFinite(p.value));
  if (!itens.length) return null;
  return (
    <div className="op-dica">
      <div className="op-dica-titulo">{label}</div>
      {itens.map((p) => (
        <div key={p.dataKey} style={{ color: p.dataKey === 'outlier' ? '#e5484d' : '#1b2433' }}>
          {p.dataKey === 'outlier' ? 'Outlier' : 'Valor'}: <b>{fmt(p.value)}</b>
        </div>
      ))}
    </div>
  );
}

export default function AnaliseCategoria({ clienteId, ano, natureza = 'despesa', categoria, meses, versao, onFechar, onUsarValores, onDecisao, onIgnorarOutlier }) {
  const ehReceita = natureza === 'receita';
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState('');
  const [base, setBase] = useState('media_12m');
  const [pct, setPct] = useState('0');

  const codigo = categoria?.codigo;
  // outra categoria: limpa a anterior (num reload por ajuste, mantém a tela até chegar a nova)
  useEffect(() => { setDados(null); }, [clienteId, ano, natureza, codigo]);
  // id da última requisição: resposta de outra categoria (ou de antes do ajuste) é descartada.
  // `versao` sobe após um ajuste (outlier/decisão) e força nova leitura.
  const req = useRef(0);
  useEffect(() => {
    if (!codigo) return;
    const id = ++req.current;
    setErro('');
    axios.get(`${API}/api/orcamento-categoria-analise/`, {
      params: { cliente_id: clienteId, codigo_categoria: codigo, ano, natureza }, headers: headers(), timeout: 60000,
    }).then((r) => {
      if (id !== req.current) return;
      if (!r.data?.success) { setErro(r.data?.erro || 'Falha'); return; }
      if (String(r.data.codigo) !== String(codigo) || (r.data.natureza && r.data.natureza !== natureza)) return;
      setDados(r.data);
    }).catch((e) => { if (id === req.current) setErro(e.response?.data?.erro || 'Erro ao analisar'); });
  }, [clienteId, ano, natureza, codigo, versao]);

  // Esc fecha o painel (menos quando é o Esc que desfaz uma célula do grid)
  const fecharRef = useRef(onFechar);
  fecharRef.current = onFechar;
  useEffect(() => {
    const tecla = (e) => {
      if (e.key !== 'Escape') return;
      if (e.target && e.target.closest && e.target.closest('.op-input')) return;
      fecharRef.current?.();
    };
    window.addEventListener('keydown', tecla);
    return () => window.removeEventListener('keydown', tecla);
  }, []);

  // painel arrastável pelo cabeçalho; duplo clique devolve à lateral direita
  const [pos, setPos] = useState(null);
  const painelRef = useRef(null);
  const iniciarArraste = (e) => {
    if (e.button !== 0 || (e.target.closest && e.target.closest('button'))) return;
    const rect = painelRef.current.getBoundingClientRect();
    const dx = e.clientX - rect.left;
    const dy = e.clientY - rect.top;
    const mover = (ev) => {
      const x = Math.min(Math.max(ev.clientX - dx, 0), window.innerWidth - 120);
      // o painel inteiro fica visível: o topo não desce além de (altura da tela − altura do painel)
      const alturaPainel = Math.min(rect.height, ALTURA_FLUTUANTE());
      const y = Math.min(Math.max(ev.clientY - dy, 0), Math.max(window.innerHeight - alturaPainel, 0));
      setPos({ x, y });
    };
    const soltar = () => {
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', soltar);
      document.body.style.userSelect = '';
    };
    document.body.style.userSelect = 'none';
    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', soltar);
  };
  const estiloPos = pos
    ? { left: pos.x, top: pos.y, right: 'auto', height: ALTURA_FLUTUANTE(), maxHeight: `calc(100vh - ${pos.y}px)`,
        borderRadius: 12, resize: 'both', overflow: 'hidden', minWidth: 380, minHeight: 320 }
    : undefined;

  const serieGrafico = useMemo(() => {
    if (!dados) return [];
    const outs = new Set((dados.outliers || []).map((o) => o.mes));
    return (dados.serie || []).map((p) => ({ mes: p.mes.slice(2).replace('-', '/'), valor: p.valor, outlier: outs.has(p.mes) ? p.valor : null }));
  }, [dados]);

  // calculadora: valor-base × (1 + pct) em cada um dos 12 meses (sazonalidade respeitada se houver)
  const preview = useMemo(() => {
    if (!dados) return [];
    const c = dados.cortes || {};
    const valorBase = base === 'p50'
      ? null
      : (base === 'mediana' ? c.mediana : c[base]) || 0;
    const fator = 1 + (parseFloat(String(pct).replace(',', '.')) || 0) / 100;
    const fat = (dados.sazonalidade?.aplicada ? dados.sazonalidade.fatores : Array(12).fill(1));
    return meses.map((m, i) => {
      const p50 = (dados.projecao || [])[i]?.p50;
      const v = base === 'p50' ? (p50 || 0) * fator : valorBase * fator * fat[parseInt(m.slice(5, 7), 10) - 1];
      return { mes: m, valor: Math.round(v * 100) / 100 };
    });
  }, [dados, base, pct, meses]);

  if (!categoria) return null;
  const titulos = dados?.titulos || [];
  return (
    <aside ref={painelRef} className="op-lateral" style={estiloPos} role="dialog" aria-label={`Análise ${categoria.nome}`}>
      <div className="op-lateral-head" onPointerDown={iniciarArraste} onDoubleClick={() => setPos(null)}
        style={{ cursor: 'move', touchAction: 'none' }} title="Arraste para mover · duplo clique volta para a lateral">
        <div>
          <div className="pa-eyebrow">
            <span className={`op-natureza ${natureza}`}>{ehReceita ? 'Receita' : 'Despesa'}</span>
            {categoria.codigo} · {dados?.grupo_dre || categoria.grupo_dre || '—'}
          </div>
          <h3 className="m-0" style={{ fontSize: 17 }}><span className={`op-selo ${categoria.selo}`}></span>{categoria.nome}</h3>
        </div>
        <button className="pa-icon-btn" onClick={onFechar} aria-label="Fechar"><i className="fas fa-times"></i></button>
      </div>
      <div className="op-lateral-body">
        {erro && <div className="alert alert-danger py-2">{erro}</div>}
        {!dados && !erro && <div className="text-muted"><i className="fas fa-spinner fa-spin me-2"></i>Analisando…</div>}
        {dados && (
          <>
            <div className="op-kpis">
              <div className="op-kpi"><div className="n">Média 3 m</div><div className="v">{fmt(dados.cortes.media_3m)}</div></div>
              <div className="op-kpi"><div className="n">Média 6 m</div><div className="v">{fmt(dados.cortes.media_6m)}</div></div>
              <div className="op-kpi"><div className="n">Média 12 m</div><div className="v">{fmt(dados.cortes.media_12m)}</div></div>
              <div className="op-kpi"><div className="n">Mediana</div><div className="v">{fmt(dados.cortes.mediana)}</div></div>
              <div className="op-kpi"><div className="n">Ano a ano</div>
                <div className="v">{dados.cortes.variacao_ano_a_ano === null || dados.cortes.variacao_ano_a_ano === undefined ? '—' : `${(dados.cortes.variacao_ano_a_ano * 100).toFixed(1)}%`}</div></div>
              <div className="op-kpi"><div className="n">CV</div><div className="v">{((dados.base?.cv || 0) * 100).toFixed(0)}%</div></div>
            </div>
            <div className="small text-muted mb-2">
              {dados.familia} · {dados.base?.n_com_mov ?? dados.base?.com_movimento} de {dados.base?.n_meses ?? dados.base?.meses} meses com movimento
              {Number.isFinite(dados.base?.frac) && (
                ` · frequência últimos 12 m: ${Math.round(dados.base.frac * 100)}%`
                + (Number.isFinite(dados.base?.frac_total) ? ` (histórico ${Math.round(dados.base.frac_total * 100)}%)` : '')
              )}
              {dados.tendencia?.aplicada && ` · tendência ${(dados.tendencia.pct_ano * 100).toFixed(1)}% a.a.`}
              {dados.sazonalidade?.aplicada && ' · sazonalidade aplicada'}
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <ComposedChart data={serieGrafico} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef1f6" vertical={false} />
                <XAxis dataKey="mes" tick={{ fontSize: 10 }} interval={5} />
                <YAxis tick={{ fontSize: 10 }} tickFormatter={(v) => `${Math.round(v / 1000)}k`} />
                <Tooltip content={<DicaGrafico />} />
                <Bar dataKey="valor" name="Valor" fill="#00205b" radius={[3, 3, 0, 0]} />
                <Scatter dataKey="outlier" name="Outlier" fill="#e5484d" />
              </ComposedChart>
            </ResponsiveContainer>
            {dados.outliers?.length > 0 && (
              <div className="mt-2 small">
                <b className="op-outlier">Outliers</b> (acima de 3 desvios no log):
                {dados.outliers.map((o) => (
                  <div key={o.mes} className="d-flex justify-content-between align-items-center py-1">
                    <span>{o.mes} · {fmt(o.valor)} · {o.x_mediana}× a mediana{o.ignorado ? ' · ignorado' : ''}</span>
                    <button className="pa-btn pa-btn-ghost" style={{ padding: '2px 8px', fontSize: 11 }}
                      onClick={() => onIgnorarOutlier(natureza, categoria.codigo, o.mes, !!o.ignorado)}>
                      {o.ignorado ? 'reconsiderar' : 'ignorar no cálculo'}
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="op-calc">
              <div className="pa-eyebrow mb-2">Calculadora</div>
              <div className="linha">
                <select className="pa-input" value={base} onChange={(e) => setBase(e.target.value)} style={{ maxWidth: 190 }}>
                  <option value="media_3m">Média 3 meses</option>
                  <option value="media_6m">Média 6 meses</option>
                  <option value="media_12m">Média 12 meses</option>
                  <option value="mediana">Mediana</option>
                  <option value="p50">P50 da projeção</option>
                </select>
                <span>×</span>
                <input className="pa-input" style={{ width: 90 }} value={pct} onChange={(e) => setPct(e.target.value)} />
                <span>%</span>
                <span className="ms-auto fw-bold">{fmt(preview.reduce((s, p) => s + p.valor, 0))} / ano</span>
              </div>
              <div className="small text-muted mt-2">
                {preview.slice(0, 12).map((p) => `${p.mes.slice(5)}: ${Math.round(p.valor / 1000)}k`).join(' · ')}
              </div>
              <div className="mt-2 d-flex gap-2">
                <button className="pa-btn pa-btn-primary" onClick={() => onUsarValores(natureza, categoria.codigo, preview)}>
                  <i className="fas fa-check me-1"></i>Usar estes valores
                </button>
                <button className="pa-btn pa-btn-ghost" onClick={() => onDecisao(natureza, categoria.codigo, !!dados.decisao)}>
                  <i className="fas fa-gavel me-1"></i>{dados.decisao ? 'Deixar de ser decisão' : 'Marcar como decisão'}
                </button>
              </div>
            </div>
            <div className="pa-eyebrow mb-1">
              Títulos{dados.titulos_ate ? ` até ${fmtData(dados.titulos_ate)}` : ' na janela'} ({titulos.length})
            </div>
            <table className="op-table">
              {/* na receita o campo `fornecedor` traz o cliente */}
              <thead><tr><th>Data</th><th>{ehReceita ? 'Cliente' : 'Fornecedor'}</th><th style={{ textAlign: 'right' }}>Valor</th></tr></thead>
              <tbody>
                {titulos.slice(0, 80).map((t_, i) => (
                  <tr key={`${t_.codigo}-${i}`}><td>{fmtData(t_.data)}</td><td>{t_.fornecedor}</td><td style={{ textAlign: 'right' }}>{fmt(t_.valor)}</td></tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </aside>
  );
}
